import React, { useState, useEffect } from "react";
import TransactionUploader from "../components/TransactionUploader";
import TransactionDateRange from "../components/TransactionDateRange";
import DateRangeSelector from "../components/DateRangeSelector";
import MonthlySummaryBoxes from "../components/MonthlySummaryBoxes";
import MonthlyExpensesTab from "../components/MonthlyExpensesTab";
import CategoryTab from "../components/CategoryTab";
import TrajectoryTab from "../components/TrajectoryTab";
import MonthlyExpensesEditor from "../components/MonthlyExpensesEditorNew";
import KeywordEditor from "../components/KeywordEditorNew";
import TransactionsTab from "../components/TransactionsTab";
import RecategorizationPanel from "../components/RecategorizationPanel";
import {
  useTransactions,
  useFinancialSummary,
  useCategorySpending,
  useDailySpending,
  useBudgetComparison,
  useMonthlyExpenses,
  useRefreshAllData,
} from "../hooks/useApi";

const DashboardPage = () => {
  // Initialize activeTab from localStorage or default to "category"
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const savedTab = localStorage.getItem("activeTab");
      return savedTab || "category";
    } catch (error) {
      console.error("Error accessing localStorage:", error);
      return "category";
    }
  });
  const [dateRange, setDateRange] = useState(null); // { startDate, endDate, label }

  // Get the comprehensive data refresh function
  const refreshAllData = useRefreshAllData();

  // Build query parameters for date filtering
  const queryParams = dateRange
    ? {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }
    : {};

  // React Query hooks for data fetching
  const { data: transactions = [], isPending: transactionsPending, isPlaceholderData: transactionsPlaceholder } =
    useTransactions(queryParams);
  const { data: financialSummary = {}, isPending: summaryPending } =
    useFinancialSummary(queryParams);
  const { 
    data: spendByCategory = [], 
    isPending: categoryPending, 
  } = useCategorySpending(queryParams);
  const { data: dailySpend = [], isPending: dailyPending } =
    useDailySpending(queryParams);
  const { data: budgetComparison = [], isPending: budgetPending } =
    useBudgetComparison(queryParams);
  const {
    data: monthlyData = { income: [], expenses: [], budget: [] },
    isPending: monthlyPending,
  } = useMonthlyExpenses();

  // Derived state from financial summary
  const totalSpent = financialSummary.total_spent || 0;
  const projectedTotal = financialSummary.projected_monthly_total || 0;
  const avgDailySpend = financialSummary.avg_daily_spend || 0;
  const avgTransactionAmount = financialSummary.avg_transaction_amount || 0;
  const transactionCount = financialSummary.total_transaction_count || 0;
  const remainingBalance = financialSummary.remaining_balance ?? null;
  const projectedDrawdown = financialSummary.projected_drawdown ?? null;
  const targetDailySpending = financialSummary.target_daily_spending ?? null;
  const daysUntilNegative = financialSummary.days_until_negative ?? null;
  const showProjections = financialSummary.show_projections ?? false;

  // Overall loading states
  // Initial loading - when no data exists yet (first time loading)
  const initialLoading =
    transactionsPending ||
    summaryPending ||
    categoryPending ||
    dailyPending ||
    budgetPending ||
    monthlyPending;

  // Function to handle tab changes and save to localStorage
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    try {
      localStorage.setItem("activeTab", tab);
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  };

  // Handle date range changes - React Query will automatically refetch with new params
  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    // React Query hooks will automatically refetch when queryParams change
  };

  // Handle successful transaction upload - React Query cache will automatically update
  const handleTransactionUpload = () => {
    // React Query mutations will handle cache invalidation automatically
    // Switch to transactions tab to view new data
    handleTabChange("transactions");
  };

  // Handle transaction updates from TransactionsTab - React Query handles cache updates
  const handleTransactionsUpdate = () => {
    // React Query mutations will handle cache invalidation automatically
    // No need to manually refresh data
  };

  // Handle monthly data updates from MonthlyExpensesEditor - React Query handles cache updates
  const refreshMonthlyDataAndStats = () => {
    // React Query mutations will handle cache invalidation automatically
    // But trigger a comprehensive refresh as a fallback to ensure all components update
    refreshAllData();
  };

  // React Query hooks handle data fetching automatically
  // Log the BigQuery-calculated values when data is available
  useEffect(() => {
    if (financialSummary && Object.keys(financialSummary).length > 0) {
      console.log("BigQuery summary data:", financialSummary);
      console.log("BigQuery category data:", spendByCategory);
      console.log(
        `BigQuery data - Daily Avg Spend: ${avgDailySpend.toFixed(
          2,
        )}, Avg Transaction: ${avgTransactionAmount.toFixed(2)}`,
      );
    }
  }, [financialSummary, spendByCategory, avgDailySpend, avgTransactionAmount]);

  // Format dates in a friendly way
  const formatDate = (dateValue) => {
    // Handle BigQuery date format which returns {value: "YYYY-MM-DD"}
    const dateString =
      typeof dateValue === "object" && dateValue?.value
        ? dateValue.value
        : dateValue;

    const options = { weekday: "short", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Only show full-screen loading on initial load, not when refetching
  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">
            Loading your spending data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-purple-700">
            Velasquez Spending Dashboard
          </h1>
          <TransactionDateRange transactions={transactions} />
        </div>

        {/* Date Range Selector */}
        <DateRangeSelector
          onDateRangeChange={handleDateRangeChange}
          initialStartDate={dateRange?.startDate}
          initialEndDate={dateRange?.endDate}
        />

        {/* Subtle loading indicator when refetching */}
        {transactionsPlaceholder && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-2 rounded-md mb-4 flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 mr-2"></div>
            <span className="text-sm">
              Updating data for selected date range...
            </span>
          </div>
        )}

        {/* Monthly Summary Boxes */}
        <MonthlySummaryBoxes
          income={monthlyData.income}
          expenses={monthlyData.expenses}
          budget={monthlyData.budget}
          totalSpent={totalSpent}
          remainingBalance={remainingBalance}
          showProjections={showProjections}
          projectedDrawdown={projectedDrawdown}
          targetDailySpending={targetDailySpending}
          daysUntilNegative={daysUntilNegative}
          avgDailySpend={avgDailySpend}
          transactionCount={transactionCount}
          avgTransactionAmount={avgTransactionAmount}
          projectedTotal={projectedTotal}
        />

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex flex-wrap border-b overflow-x-auto">
            <button
              className={`px-3 py-3 text-sm font-medium ${
                activeTab === "category"
                  ? "text-purple-700 border-b-2 border-purple-500"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("category")}
            >
              Spending by Category
            </button>
            <button
              className={`px-3 py-3 text-sm font-medium ${
                activeTab === "expenses"
                  ? "text-purple-700 border-b-2 border-purple-500"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("expenses")}
            >
              Monthly Expenses
            </button>
            <button
              className={`px-3 py-3 text-sm font-medium ${
                activeTab === "trajectory"
                  ? "text-purple-700 border-b-2 border-purple-500"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("trajectory")}
            >
              Monthly Trajectory
            </button>
            <button
              className={`px-3 py-3 text-sm font-medium ${
                activeTab === "transactions"
                  ? "text-purple-700 border-b-2 border-purple-500"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("transactions")}
            >
              Transactions
            </button>
            <button
              className={`px-3 py-3 text-sm font-medium ${
                activeTab === "upload"
                  ? "text-purple-700 border-b-2 border-purple-500"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("upload")}
            >
              Upload Transactions
            </button>
            <button
              className={`px-3 py-3 text-sm font-medium ${
                activeTab === "monthlyExpenses"
                  ? "text-purple-700 border-b-2 border-purple-500"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("monthlyExpenses")}
            >
              Monthly Income/Expenses Editor
            </button>
            <button
              className={`px-3 py-3 text-sm font-medium ${
                activeTab === "keywordEditor"
                  ? "text-purple-700 border-b-2 border-purple-500"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("keywordEditor")}
            >
              Keyword Editor
            </button>
            <button
              className={`px-3 py-3 text-sm font-medium ${
                activeTab === "recategorization"
                  ? "text-purple-700 border-b-2 border-purple-500"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => handleTabChange("recategorization")}
            >
              Re-categorization
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-2 sm:p-4">
            {/* Category Tab */}
            {activeTab === "category" && (
              <CategoryTab
                spendByCategory={spendByCategory}
                transactions={transactions}
                formatCurrency={formatCurrency}
                budgetComparison={budgetComparison}
              />
            )}

            {/* Trajectory Tab */}
            {activeTab === "trajectory" && (
              <TrajectoryTab
                dailySpend={dailySpend}
                projectedTotal={projectedTotal}
                avgDailySpend={avgDailySpend}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                daysUntilNegative={daysUntilNegative}
                currentDay={financialSummary.current_day || 9}
                daysInMonth={financialSummary.days_in_month || 31}
                remainingBalance={remainingBalance ?? 0}
                projectedDrawdown={projectedDrawdown ?? 0}
                showProjections={showProjections}
              />
            )}

            {/* Transactions Tab */}
            {activeTab === "transactions" && (
              <TransactionsTab
                transactions={transactions}
                spendByCategory={spendByCategory}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                onTransactionsUpdate={handleTransactionsUpdate}
                budgetComparison={budgetComparison}
              />
            )}

            {/* Upload Tab */}
            {activeTab === "upload" && (
              <div>
                <h2 className="text-lg font-semibold text-gray-700 mb-4">
                  Upload Transactions
                </h2>
                <p className="text-gray-600 mb-4">
                  Connect a bank account, or upload an Amazon order history
                  spreadsheet to assign those card charges to one person.
                </p>
                <TransactionUploader
                  onUploadSuccess={handleTransactionUpload}
                />
              </div>
            )}

            {/* Monthly Expenses Tab */}
            {activeTab === "expenses" && (
              <MonthlyExpensesTab
                expenses={monthlyData.expenses}
                budgetComparison={budgetComparison}
                dateRange={dateRange}
                income={monthlyData.income}
              />
            )}

            {/* Monthly Expenses Editor */}
            {activeTab === "monthlyExpenses" && (
              <MonthlyExpensesEditor
                onSaveSuccess={refreshMonthlyDataAndStats}
              />
            )}
            {/* Keyword Editor */}
            {activeTab === "keywordEditor" && <KeywordEditor onSaveSuccess={refreshAllData} />}
            
            {/* Re-categorization Panel */}
            {activeTab === "recategorization" && <RecategorizationPanel onRecategorizeComplete={refreshAllData} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;


